
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAgingProcessCase,
  getAgingSettledCase,
  getAllSchemesForDashboard,
  getBusinessSnapshot,
  getCghsStatusTable,
  getCghsStatusTableList,
  getDeduction,
  getDeductionDetailsData,
  getDSOData,
  getMonthOnMonthClaimStatus,
  getPatientListAgingSettledCase,
  getSchemeTableData,
} from "../../models/dashboardSlice";
import { Select, Switch, DatePicker, Row, Col, Card, message } from "antd";
import { AppDispatch, RootState } from "../../store/store";
import dayjs from "dayjs";
import "./dashboardCss.css";
// import { downloadDashboardData } from "../../apis/dashboardService";
import Dashboard from "./NewDashboard";
import {
  downloadCghsStatusTableList,
  downloadPatientListAgingSettledCase,
  DownloadSchemeData,
} from "../../apis/dashboardService";

const { RangePicker } = DatePicker;

const DashboardContainer: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { loading } = useSelector((state: RootState) => state.dashboard);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [downloadDocument, setDownloadDocument] = useState<string>("No");
  const { hospital } = useSelector((state: RootState) => state);
  const handleOpenModal = () => {
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
  };
  const [businessDateRange, setBusinessDateRange] = useState<[any, any]>([
    dayjs().startOf("month"), // Default start date (beginning of the current month)
    dayjs().endOf("month"), // Default end date (end of the current month)
  ]);

  const [monthOnMonthDateRange, setMonthOnMonthDateRange] = useState<
    [any, any]
  >([
    dayjs().startOf("month"), // Default start date (beginning of the current month)
    dayjs().endOf("month"), // Default end date (end of the current month)
  ]);
  const [handleDownloadButton, setHandleDownloadButton] = useState({
    scheme: false,
    status: false,
    interval: false,
  });
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [schemeId, setSchemeId] = useState(-1);
  const [visitType, setVisitType] = useState(1);
  const [amtPercent, setAmtPercent] = useState("count");
  const [selectedScheme, setSelectedScheme] = useState<string | null>(null);
  const [rejectionCaseType, setRejectionCaseType] = useState("IP");
  const [rejectionValue, setRejectionValue] = useState(true);
  const [openCasesValue, setOpenCasesValue] = useState(true);
  const [closedCasesValue, setClosedCasesValue] = useState(true);

  const downloadFile = (fileBytes: string, fileName: string) => {
    if (!fileBytes) {
      message.error("Failed to retrieve file data.");
      return;
    }

    // Convert Base64 string to Uint8Array
    const byteCharacters = atob(fileBytes);
    const byteNumbers = new Array(byteCharacters.length)
      .fill(0)
      ?.map((_, i) => byteCharacters.charCodeAt(i));
    const byteArray = new Uint8Array(byteNumbers);

    // Create a Blob with the correct MIME type
    const blob = new Blob([byteArray], {
      type: "application/vnd.ms-excel",
    });

    // Create a download URL and trigger the download
    const downloadUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = `${fileName}.xls`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(downloadUrl);
  };

  const handleDownload = async (
    schemeName: string,
    month: string,
    settled: string
  ) => {
    if (hospital?.selectedHospital?.value) {
      setDownloadLoading(true); // Show loader

      try {
        let result;

        if (
          handleDownloadButton.scheme &&
          !handleDownloadButton.interval &&
          !handleDownloadButton.status
        ) {
          result = await DownloadSchemeData(
            hospital?.selectedHospital?.value,
            businessDateRange[0].format("YYYY-MM-DD"),
            businessDateRange[1].format("YYYY-MM-DD"),
            schemeName
          );
        } else if (
          handleDownloadButton.status &&
          !handleDownloadButton.scheme &&
          !handleDownloadButton.interval
        ) {
          result = await downloadCghsStatusTableList(
            hospital?.selectedHospital?.value,
            monthOnMonthDateRange[0].format("YYYY-MM-DD"),
            monthOnMonthDateRange[1].format("YYYY-MM-DD"),
            schemeId,
            visitType,
            schemeName
          );
        } else {
          result = await downloadPatientListAgingSettledCase(
            hospital?.selectedHospital?.value,
            schemeId,
            visitType,
            schemeName,
            month,
            settled
          );
        }

        if (result && result.fileBytes) {
          downloadFile(result.fileBytes, schemeName);
        } else {
          message.error("Failed to retrieve file data.");
        }
      } catch (err: any) {
        message.error(err.message || "Download failed");
      } finally {
        setDownloadLoading(false); // Hide loader
      }
    }
  };

  useEffect(() => {
    if (hospital?.selectedHospital?.value)
      dispatch(
        getBusinessSnapshot({
          hospitalId: hospital?.selectedHospital?.value,
          startDate: businessDateRange[0].format("YYYY-MM-DD"),
          endDate: businessDateRange[1].format("YYYY-MM-DD"),
        })
      );
  }, [dispatch, businessDateRange, hospital?.selectedHospital?.value]);

  useEffect(() => {
    if (hospital?.selectedHospital?.value)
      dispatch(
        getDSOData({
          hospitalId: hospital?.selectedHospital?.value,
          startDate: businessDateRange[0].format("YYYY-MM-DD"),
          endDate: businessDateRange[1].format("YYYY-MM-DD"),
        })
      );
  }, [dispatch, businessDateRange, hospital?.selectedHospital?.value]);

  useEffect(() => {
    if (hospital?.selectedHospital?.value)
      dispatch(
        getDeductionDetailsData({
          hospitalId: hospital?.selectedHospital?.value,
          startDate: monthOnMonthDateRange[0].format("YYYY-MM-DD"),
          endDate: monthOnMonthDateRange[1].format("YYYY-MM-DD"),
          schemeId: schemeId,
          visitType: visitType,
          type: amtPercent,
        })
      );
  }, [
    dispatch,
    monthOnMonthDateRange,
    schemeId,
    visitType,
    amtPercent,
    hospital?.selectedHospital?.value,
  ]);

  useEffect(() => {
    if (hospital?.selectedHospital?.value)
      dispatch(
        getDeduction({
          hospitalId: hospital?.selectedHospital?.value,
          startDate: monthOnMonthDateRange[0].format("YYYY-MM-DD"),
          endDate: monthOnMonthDateRange[1].format("YYYY-MM-DD"),
          schemeId: schemeId,
          visitType: visitType,
          type: amtPercent,
        })
      );
  }, [
    dispatch,
    monthOnMonthDateRange,
    schemeId,
    visitType,
    amtPercent,
    hospital?.selectedHospital?.value,
  ]);

  useEffect(() => {
    if (hospital?.selectedHospital?.value)
      dispatch(
        getAgingProcessCase({
          hospitalId: hospital?.selectedHospital?.value,
          schemeId: schemeId,
          visitType: visitType,
        })
      );
  }, [dispatch, schemeId, visitType, hospital?.selectedHospital?.value]);

  useEffect(() => {
    if (hospital?.selectedHospital?.value)
      dispatch(
        getAgingSettledCase({
          hospitalId: hospital?.selectedHospital?.value,
          schemeId: schemeId,
          visitType: visitType,
        })
      );
  }, [dispatch, schemeId, visitType, hospital?.selectedHospital?.value]);

  useEffect(() => {
    if (
      hospital?.selectedHospital?.value &&
      (schemeId === -1 || schemeId === 2)
    )
      dispatch(
        getMonthOnMonthClaimStatus({
          hospitalId: hospital?.selectedHospital?.value,
          startDate: monthOnMonthDateRange[0].format("YYYY-MM-DD"),
          endDate: monthOnMonthDateRange[1].format("YYYY-MM-DD"),
          schemeId: schemeId,
          visitType: visitType,
        })
      );
  }, [
    dispatch,
    monthOnMonthDateRange,
    schemeId,
    visitType,
    hospital?.selectedHospital?.value,
  ]);

  useEffect(() => {
    if (hospital?.selectedHospital?.value && schemeId === 1)
      dispatch(
        getCghsStatusTable({
          hospitalId: hospital?.selectedHospital?.value,
          startDate: monthOnMonthDateRange[0].format("YYYY-MM-DD"),
          endDate: monthOnMonthDateRange[1].format("YYYY-MM-DD"),
          schemeId: schemeId,
          visitType: visitType,
        })
      );
  }, [
    dispatch,
    monthOnMonthDateRange,
    schemeId,
    visitType,
    hospital?.selectedHospital?.value,
  ]);

  const onPayerNameClick = async (schemeName: string) => {
    if (hospital?.selectedHospital?.value)
      dispatch(
        getSchemeTableData({
          hospitalId: hospital?.selectedHospital?.value,
          startDate: businessDateRange[0].format("YYYY-MM-DD"),
          endDate: businessDateRange[1].format("YYYY-MM-DD"),
          schemeName: schemeName,
        })
      );
  };

  const onIntervalClick = async (
    month: string,
    interval: string,
    settled: string
  ) => {
    if (hospital?.selectedHospital?.value)
      dispatch(
        getPatientListAgingSettledCase({
          hospitalId: hospital?.selectedHospital?.value,
          schemeId: schemeId,
          visitType: visitType,
          interval: interval,
          month: month,
          settled: settled,
        })
      );
  };

  const onStatusClick = async (status: string) => {
    dispatch(
      getCghsStatusTableList({
        hospitalId: hospital?.selectedHospital?.value,
        startDate: monthOnMonthDateRange[0].format("YYYY-MM-DD"),
        endDate: monthOnMonthDateRange[1].format("YYYY-MM-DD"),
        schemeId: schemeId,
        visitType: visitType,
        status: status,
      })
    );
  };

  useEffect(() => {
    if (hospital?.selectedHospital?.value)
      dispatch(
        getAllSchemesForDashboard({
          hospitalId: hospital?.selectedHospital?.value,
        })
      );
  }, [dispatch, hospital?.selectedHospital?.value]);

  // if (loading) return <div>Loading...</div>;

  return (
    <Dashboard
      businessDateRange={businessDateRange}
      setBusinessDateRange={setBusinessDateRange}
      monthOnMonthDateRange={monthOnMonthDateRange}
      setMonthOnMonthDateRange={setMonthOnMonthDateRange}
      schemeId={schemeId}
      setSchemeId={setSchemeId}
      setVisitType={setVisitType}
      setAmtPercent={setAmtPercent}
      amtPercent={amtPercent}
      onPayerNameClick={onPayerNameClick}
      onIntervalClick={onIntervalClick}
      onStatusClick={onStatusClick}
      downloadDocument={downloadDocument}
      setDocumentDownload={setDownloadDocument}
      setSelectedScheme={setSelectedScheme}
      selectedScheme={selectedScheme}
      handleDownload={handleDownload}
      setHandleDownloadButton={setHandleDownloadButton}
      handleDownloadButton={handleDownloadButton}
      downloadLoading={downloadLoading}
    />
  );
};

export default DashboardContainer;